<template>
  <div class="custom-form-group">
    <label v-if="label" :for="id" class="custom-label">{{ label }}</label>

    <!-- TEXT / NUMBER / PASSWORD / MAIL -->
    <div 
      v-if="['text', 'Number', 'password', 'mail', 'number', 'time'].includes(type)"
      class="custom-input-wrapper"
      :class="{ 'has-button': showButton && !disabled }"
    >
      <input
        :id="id"
        :type="type === 'Number' ? 'number' : type"
        class="custom-input shadow-none"
        :class="{ 'is-disabled': disabled }"
        :placeholder="placeholder"
        v-model="inputValue"
        @keyup.enter="onSubmit"
        :disabled="disabled"
      />
      <button 
        v-if="showButton && !disabled" 
        class="append-button" 
        type="button"
        @click="onSubmit"
      >
        <img :src="icon" alt="icon" height="15" />
      </button>
    </div>

    <!-- DATE -->
    <div v-else-if="type === 'date'" class="custom-input-wrapper">
      <Vue-date-picker
        format="DD-MM-YYYY"
        value-type="format"
        v-model="inputValue"
        :placeholder="placeholder"
        class="custom-datepicker"
        :disabled="disabled"
      />
    </div>

    <!-- SELECT -->
    <div v-else-if="type === 'select'">
      <b-form-select
        :id="id"
        v-model="inputValue"
        :options="selectOptions"
        class="custom-select-input shadow-none"
        :disabled="disabled"
      />
    </div>

    <!-- TEXTAREA -->
    <textarea
      v-else-if="type === 'area'"
      :id="id"
      class="custom-input custom-textarea shadow-none"
      :placeholder="placeholder"
      v-model="inputValue"
      :rows="areaRows || 3"
      :disabled="disabled"
    ></textarea>

    <!-- TAGS -->
    <b-form-tags
      v-else-if="type === 'tags'"
      v-model="inputValue"
      separator=",;"
      :placeholder="placeholder"
      class="custom-tags shadow-none"
      :disabled="disabled"
    />
  </div>
</template>

<script>
export default {
  name: 'CustomInput',
  emits: ['update:modelValue', 'input', 'submit'],
  props: {
    modelValue: { type: [String, Number, Object, Date, Array], default: '' },
    type: { type: String, default: 'text' },
    label: { type: String, default: '' },
    id: { type: String, default: () => `input-${Math.random().toString(36).substr(2, 9)}` },
    placeholder: { type: String, default: 'Ajouter un élément' },
    icon: { type: String, default: require('@/assets/icons/add-wihte.svg') },
    options: { type: Array, default: () => [] },
    labelKey: { type: String, default: 'label' },
    showButton: { type: Boolean, default: true },
    nullableVSelecte: { type: Boolean, default: true },
    reduce: { type: Function, default: null },
    areaRows: { type: Number, default: null },
    disabled: { type: Boolean, default: false }
  },
  computed: {
    selectOptions() {
      const mappedOptions = this.options.map(option => {
        if (typeof option !== 'object' || option === null) {
          return { value: option, text: option };
        }

        const text = option[this.labelKey] || option.label || option.name || option.value || option.text || '';
        const value = this.reduce ? this.reduce(option) : (Object.prototype.hasOwnProperty.call(option, 'value') ? option.value : option);

        return { value, text };
      });

      if (!this.nullableVSelecte) {
        return mappedOptions;
      }

      return [
        { value: null, text: this.placeholder, disabled: true },
        ...mappedOptions,
      ];
    },

    inputValue: {
      get() {
        if (this.type === 'tags' && typeof this.modelValue === 'string') {
          return this.modelValue.split(',').map(tag => tag.trim()).filter(t => t.length > 0);
        }
        return this.modelValue;
      },
      set(val) {
        if (this.type === 'tags' && Array.isArray(val)) {
          this.emitValue(val.join(', '));
        } else {
          this.emitValue(val);
        }
      }
    }
  },
  methods: {
    emitValue(val) {
      this.$emit('update:modelValue', val);
      this.$emit('input', val);
    },
    onSubmit() {
      if (!this.disabled) this.$emit('submit', this.inputValue);
    }
  }
};
</script>

<style scoped>
/* --- Global Variables simulation --- */
.custom-form-group {
  margin-bottom: 1rem;
}

/* --- Labels --- */
.custom-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #475569; /* Slate 600 */
  margin-bottom: 6px;
  text-align: left;
}

/* --- Base Input Style --- */
.custom-input-wrapper {
  position: relative;
  display: flex;
  width: 100%;
}

.custom-input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  color: #1e293b;
  background-color: #ffffff;
  border: 1px solid #e2e8f0; /* Slate 200 */
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
}

.custom-input:focus {
  outline: none;
  border-color: #6366f1; /* Indigo 500 */
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
}

.custom-input::placeholder {
  color: #94a3b8;
}

/* --- Buttons --- */
.append-button {
  background-color: #6366f1;
  border: none;
  border-radius: 0 8px 8px 0;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  cursor: pointer;
  margin-left: -1px;
}

.append-button:hover {
  background-color: #4f46e5;
}

.custom-input-wrapper.has-button .custom-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* --- Textarea --- */
.custom-textarea {
  resize: vertical;
  min-height: 80px;
}

/* --- Tags --- */
:deep(.custom-tags) {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  padding: 4px !important;
}
:deep(.custom-tags .b-form-tag) {
  background-color: #6366f1 !important;
  border-radius: 4px;
}

/* --- Form Select --- */
.custom-select-input {
  height: 43px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 38px 9px 14px;
  font-size: 14px;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
}

.custom-select-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
}

/* --- Disabled State --- */
.is-disabled,
.custom-select-input:disabled {
  background-color: #f8fafc !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

/* --- Datepicker Styling --- */
:deep(.mx-input) {
  height: 43px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: none;
}

:deep(.mx-input:focus) {
  border-color: #6366f1;
}
</style>
